/**
 * `<Toast/>` — the corner confirmation a host raises after an action lands
 * ("Applied 3 changes", what a seat move did). #2172, the laptop motion
 * pass: it slides up from the bottom edge and, when the host clears it,
 * slides back down instead of vanishing — never a pop.
 *
 * The host owns the message and its timer, exactly as before; `null`
 * means "not showing". On `null` the last message stays on screen for the
 * exit (`data-state="closed"`, styles/motion-shell.css) and is dropped on
 * that animation's end. Where JS motion is off (reduced motion, tests) it
 * is dropped at once, so a test that waits out the timer sees it gone.
 */
import { type AnimationEvent, useState } from "react";

import { cn } from "../../lib/cn";
import { jsMotionEnabled } from "../../lib/motionTokens";

export interface ToastProps {
  /** The text to show; `null` hides it (with the slide-out). */
  message: string | null;
  testid: string;
  className?: string | undefined;
}

export function Toast({ message, testid, className }: ToastProps) {
  const [animated] = useState(jsMotionEnabled);
  // The message on screen: the live one, or the last one while it leaves.
  const [shown, setShown] = useState<string | null>(message);
  if (message !== null && message !== shown) setShown(message);
  if (message === null && shown !== null && !animated) setShown(null);

  if (shown === null) return null;
  const closing = message === null;
  return (
    <output
      data-testid={testid}
      data-state={closing ? "closed" : "open"}
      className={cn(
        "motion-toast fixed bottom-4 right-4 z-50 rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg shadow-lg",
        className,
      )}
      onAnimationEnd={(event: AnimationEvent<HTMLOutputElement>) => {
        if (closing && event.target === event.currentTarget) setShown(null);
      }}
    >
      {shown}
    </output>
  );
}
